/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Image Processing Overlay - Shimmer, pulse animations and visual feedback
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';

interface ImageProcessingOverlayProps {
  isLoading: boolean;
  message?: string;
  messages?: string[]; // Multiple messages for rotation
  rotationInterval?: number;
  progress?: number; // 0-100
  showShimmer?: boolean;
  showPulse?: boolean;
}

const ImageProcessingOverlay: React.FC<ImageProcessingOverlayProps> = ({
  isLoading,
  message,
  messages,
  rotationInterval = 3000,
  progress,
  showShimmer = true,
  showPulse = true
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Rotate messages if multiple are provided
  useEffect(() => {
    if (isLoading && messages && messages.length > 0) {
      const interval = setInterval(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
      }, rotationInterval);
      return () => clearInterval(interval);
    }
  }, [isLoading, messages, rotationInterval]);

  if (!isLoading) return null;

  const displayMessage = messages && messages.length > 0 
    ? messages[currentMessageIndex] 
    : message;

  return (
    <div className="absolute inset-0 z-20">
      {/* Shimmer Effect */}
      {showShimmer && (
        <div className="absolute inset-0 overflow-hidden rounded-lg">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            animate={{
              x: ['-100%', '200%']
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear'
            }}
            style={{
              width: '50%',
              height: '100%',
              transform: 'skewX(-20deg)'
            }}
          />
        </div>
      )}

      {/* Pulse Border Effect */}
      {showPulse && (
        <motion.div
          className="absolute inset-0 border-4 border-slate-400 rounded-lg"
          animate={{
            opacity: [0.5, 1, 0.5],
            scale: [1, 1.02, 1]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      )}

      {/* Blur Overlay */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-lg" />

      {/* Processing Badge */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
        <motion.div
          className="bg-white/95 backdrop-blur-md rounded-xl shadow-xl p-6 flex flex-col items-center gap-4 min-w-[240px] max-w-sm"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Spinner */}
          <div className="relative">
            <Loader2 className="w-10 h-10 text-slate-700 animate-spin" />
          </div>

          {/* Message with Animation */}
          <div className="h-12 flex items-center justify-center text-center overflow-hidden">
             <AnimatePresence mode="wait">
                <motion.p 
                    key={displayMessage}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-sm font-medium text-slate-800"
                >
                    {displayMessage}
                </motion.p>
             </AnimatePresence>
          </div>

          {/* Progress Bar (if provided) */}
          {progress !== undefined && (
            <div className="w-full">
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-slate-700 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-[10px] text-slate-500 text-center mt-1 uppercase tracking-wider font-bold">
                {Math.round(progress)}% Processing
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ImageProcessingOverlay;